/**
 * Finance — Close-Cycle Assistant, Cash-flow forecast, Statutory exports.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Lock, Unlock, Download, LineChart, ClipboardCheck, CheckCircle2, XCircle, Clock, Sparkles, Wand2, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { CloseHealthTile, AutomationsTab } from "@/components/finance/close/Automations";
import { apiErrorMessage } from "@/lib/apiError";

const fmt = (n) => {
  try { return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(Number(n || 0)); }
  catch { return `₹${Number(n || 0).toFixed(2)}`; }
};

export default function FinanceClose() {
  const { activeWorkspaceId } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") || "close");
  useEffect(() => { setSp((p) => { const n = new URLSearchParams(p); n.set("tab", tab); return n; }, { replace: true }); }, [tab, setSp]);
  return (
    <div className="space-y-6" data-testid="finance-close-page">
      <PageHeader title="Period Close" subtitle="One-click month close, cash-flow forecast, and Indian statutory exports." icon={ClipboardCheck} />
      <CloseHealthTile wsId={activeWorkspaceId} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none flex-wrap h-auto bg-transparent p-0 border-b border-border w-full justify-start gap-1">
          <TabsTrigger value="close" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="fc-tab-close"><Lock className="w-4 h-4 mr-2" />Month Close</TabsTrigger>
          <TabsTrigger value="forecast" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="fc-tab-forecast"><LineChart className="w-4 h-4 mr-2" />Cash-flow forecast</TabsTrigger>
          <TabsTrigger value="statutory" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="fc-tab-statutory"><Download className="w-4 h-4 mr-2" />Statutory returns</TabsTrigger>
          <TabsTrigger value="automations" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="fc-tab-automations"><Wand2 className="w-4 h-4 mr-2" />Automations</TabsTrigger>
        </TabsList>
        <TabsContent value="close" className="mt-6"><CloseTab wsId={activeWorkspaceId} /></TabsContent>
        <TabsContent value="forecast" className="mt-6"><ForecastTab wsId={activeWorkspaceId} /></TabsContent>
        <TabsContent value="statutory" className="mt-6"><StatutoryTab wsId={activeWorkspaceId} /></TabsContent>
        <TabsContent value="automations" className="mt-6"><AutomationsTab wsId={activeWorkspaceId} /></TabsContent>
      </Tabs>
    </div>
  );
}

function CloseTab({ wsId }) {
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [checklist, setChecklist] = useState(null);
  const [lock, setLock] = useState(null);
  const [accts, setAccts] = useState([]);
  const [opts, setOpts] = useState({ run_depreciation: true, run_payroll: false, payroll_payment_account_id: "", payroll_tds_rate: 10, run_fx_reval: false, lock_period: false });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [aiPlan, setAiPlan] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiRun, setAiRun] = useState(null);
  const [aiRunBusy, setAiRunBusy] = useState(false);
  const [autoPost, setAutoPost] = useState(false);
  const [autoBankMatch, setAutoBankMatch] = useState(false);
  const [aiBankAcct, setAiBankAcct] = useState("");

  const loadChecklist = useCallback(() => api.get(`/finance/workspaces/${wsId}/close-cycle/checklist?period=${period}`).then((r) => setChecklist(r.data)), [wsId, period]);
  const loadLock = useCallback(() => api.get(`/finance/workspaces/${wsId}/close-cycle/lock-status`).then((r) => setLock(r.data.period_lock_date)), [wsId]);
  useEffect(() => { if (wsId) { loadChecklist(); loadLock(); } }, [wsId, period, loadChecklist, loadLock]);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/finance/workspaces/${wsId}/accounts`).then((r) => setAccts((r.data.accounts || []).filter((a) => ["bank", "cash"].includes(a.subtype))));
  }, [wsId]);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/finance/workspaces/${wsId}/close-cycle/execute`, { period, ...opts });
      setResult(data);
      toast.success(data.all_ok ? "Close completed" : "Close ran with warnings");
      loadChecklist(); loadLock();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };
  const unlock = async () => {
    try {
      await api.post(`/finance/workspaces/${wsId}/close-cycle/unlock`);
      toast.success("Period unlocked");
      loadLock();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const generatePlan = async () => {
    setAiBusy(true); setAiPlan(null);
    try {
      const { data } = await api.post(`/finance/workspaces/${wsId}/close-cycle/ai-plan`, { period });
      setAiPlan(data);
    } catch (e) { toast.error(apiErrorMessage(e, "AI plan failed")); }
    finally { setAiBusy(false); }
  };

  const orchestrate = async () => {
    if (opts.lock_period) {
      if (!window.confirm(`This will LOCK the ${period} period after close. Journals dated on/before month-end will be rejected until unlocked. Continue?`)) return;
    }
    setAiRunBusy(true); setAiRun(null);
    try {
      const body = {
        period,
        auto_post_invoices: autoPost,
        auto_smart_match_bank: autoBankMatch,
        bank_account_id: autoBankMatch ? aiBankAcct : undefined,
        run_depreciation: opts.run_depreciation,
        run_payroll: opts.run_payroll,
        payroll_payment_account_id: opts.payroll_payment_account_id || undefined,
        payroll_tds_rate: opts.payroll_tds_rate,
        run_fx_reval: opts.run_fx_reval,
        lock_period: opts.lock_period,
      };
      const { data } = await api.post(`/finance/workspaces/${wsId}/close-cycle/ai-orchestrate`, body);
      setAiRun(data);
      toast.success("AI close pipeline completed");
      loadChecklist(); loadLock();
    } catch (e) { toast.error(apiErrorMessage(e, "Orchestrate failed")); }
    finally { setAiRunBusy(false); }
  };

  return (
    <div className="space-y-6">
      <Card><CardContent className="p-6 flex items-center justify-between">
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide">Ledger lock</div>
          <div className="text-lg font-mono mt-1" data-testid="close-lock-status">
            {lock ? <><Lock className="w-4 h-4 inline mr-2 text-amber-600" />Locked through <span className="font-semibold">{lock}</span></> : <><Unlock className="w-4 h-4 inline mr-2 text-emerald-600" />Unlocked — entries allowed for any date</>}
          </div>
        </div>
        {lock && <Button variant="outline" size="sm" onClick={unlock} data-testid="close-unlock-btn"><Unlock className="w-4 h-4 mr-2" />Unlock</Button>}
      </CardContent></Card>

      <Card><CardContent className="p-6 space-y-4">
        <div className="flex items-end gap-3">
          <div><Label>Period (YYYY-MM)</Label><Input value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="close-period-input" /></div>
          <div className="flex-1" />
          <Button variant="outline" onClick={loadChecklist} data-testid="close-refresh-btn">Refresh</Button>
        </div>
        {checklist && (
          <div className="space-y-2" data-testid="close-checklist">
            {checklist.checklist.map((c) => (
              <div key={c.key} className="flex items-center justify-between border rounded p-3" data-testid={`close-check-${c.key}`}>
                <div className="flex items-center gap-3">
                  {c.ok ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <XCircle className="w-5 h-5 text-rose-600" />}
                  <div>
                    <div className="font-medium text-sm">{c.label}</div>
                    <div className="text-xs text-muted-foreground">{c.hint}</div>
                  </div>
                </div>
                <div className="text-sm">{typeof c.value === "boolean" ? (c.value ? "Yes" : "No") : c.value}</div>
              </div>
            ))}
          </div>
        )}
      </CardContent></Card>

      <AiClosePanel
        aiPlan={aiPlan} aiBusy={aiBusy} onGenerate={generatePlan}
        aiRun={aiRun} aiRunBusy={aiRunBusy} onOrchestrate={orchestrate}
        autoPost={autoPost} setAutoPost={setAutoPost}
        autoBankMatch={autoBankMatch} setAutoBankMatch={setAutoBankMatch}
        aiBankAcct={aiBankAcct} setAiBankAcct={setAiBankAcct}
        accts={accts} opts={opts}
      />

      <Card><CardContent className="p-6 space-y-4">
        <h3 className="font-semibold">One-click close options</h3>
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm" data-testid="close-opt-dep">
            <Checkbox checked={opts.run_depreciation} onCheckedChange={(v) => setOpts({ ...opts, run_depreciation: !!v })} />
            Run depreciation for the period
          </label>
          <label className="flex items-center gap-2 text-sm" data-testid="close-opt-payroll">
            <Checkbox checked={opts.run_payroll} onCheckedChange={(v) => setOpts({ ...opts, run_payroll: !!v })} />
            Run payroll for the period
          </label>
          {opts.run_payroll && (
            <div className="ml-6 grid grid-cols-2 gap-2 max-w-xl">
              <div><Label className="text-xs">Pay from account</Label>
                <Select value={opts.payroll_payment_account_id} onValueChange={(v) => setOpts({ ...opts, payroll_payment_account_id: v })}>
                  <SelectTrigger data-testid="close-payroll-acct"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>{accts.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Default TDS %</Label><Input type="number" step="0.1" value={opts.payroll_tds_rate} onChange={(e) => setOpts({ ...opts, payroll_tds_rate: Number(e.target.value) })} data-testid="close-payroll-tds" /></div>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm" data-testid="close-opt-fx">
            <Checkbox checked={opts.run_fx_reval} onCheckedChange={(v) => setOpts({ ...opts, run_fx_reval: !!v })} />
            Run FX revaluation for foreign-currency AR/AP
          </label>
          <label className="flex items-center gap-2 text-sm" data-testid="close-opt-lock">
            <Checkbox checked={opts.lock_period} onCheckedChange={(v) => setOpts({ ...opts, lock_period: !!v })} />
            Lock the period after close (prevents further posting)
          </label>
        </div>
        <Button onClick={run} disabled={busy || (opts.run_payroll && !opts.payroll_payment_account_id)} data-testid="close-execute-btn">
          {busy ? "Running…" : "▶ Execute close"}
        </Button>
      </CardContent></Card>

      {result && (
        <Card><CardContent className="p-6 space-y-2" data-testid="close-result">
          <h3 className="font-semibold">Result — {result.period}</h3>
          {result.steps.map((s, i) => (
            <div key={i} className="flex items-center gap-3 border rounded p-2" data-testid={`close-step-${s.step}`}>
              {s.status === "ok" && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
              {s.status === "skipped" && <Clock className="w-5 h-5 text-muted-foreground" />}
              {s.status === "error" && <XCircle className="w-5 h-5 text-rose-600" />}
              <div className="flex-1">
                <div className="text-sm font-medium capitalize">{s.step.replaceAll("_", " ")}</div>
                <div className="text-xs text-muted-foreground">
                  {s.status} {s.reason ? `— ${s.reason}` : ""}{s.error ? `— ${s.error}` : ""}
                  {s.detail ? ` — ${Object.entries(s.detail).map(([k, v]) => `${k}: ${v}`).join(", ")}` : ""}
                </div>
              </div>
            </div>
          ))}
        </CardContent></Card>
      )}
    </div>
  );
}

function AiClosePanel({ aiPlan, aiBusy, onGenerate, aiRun, aiRunBusy, onOrchestrate,
                         autoPost, setAutoPost, autoBankMatch, setAutoBankMatch,
                         aiBankAcct, setAiBankAcct, accts, opts }) {
  const score = aiPlan?.readiness_score;
  const scoreTone = score == null ? "text-muted-foreground" : score >= 80 ? "text-emerald-600" : score >= 50 ? "text-amber-600" : "text-rose-600";
  return (
    <Card className="border-primary/40 bg-primary/[0.03]" data-testid="ai-close-panel">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            <div>
              <div className="font-semibold">AI Close-Cycle Assistant</div>
              <div className="text-xs text-muted-foreground">Get an AI-powered readiness score, action plan, and one-click orchestrated close.</div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={onGenerate} disabled={aiBusy} data-testid="ai-close-plan-btn">
            {aiBusy ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {aiBusy ? "Analyzing…" : (aiPlan ? "Refresh plan" : "Generate close plan")}
          </Button>
        </div>

        {aiPlan && (
          <div className="space-y-3" data-testid="ai-close-plan">
            <div className="flex items-center gap-4">
              <div className="text-center">
                <div className={`text-4xl font-mono font-bold ${scoreTone}`} data-testid="ai-readiness-score">{score}%</div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Readiness</div>
              </div>
              <div className="flex-1 text-sm" data-testid="ai-narrative">{aiPlan.plan?.narrative}</div>
            </div>

            {aiPlan.plan?.blockers?.length > 0 && (
              <div className="space-y-1.5" data-testid="ai-blockers">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Blockers</div>
                {aiPlan.plan.blockers.map((b, i) => (
                  <div key={i} className="flex items-start gap-2 border rounded p-2 text-sm" data-testid={`ai-blocker-${i}`}>
                    <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${b.severity === "high" ? "text-rose-600" : b.severity === "medium" ? "text-amber-600" : "text-muted-foreground"}`} />
                    <div>
                      <div className="font-medium">{b.summary}</div>
                      <div className="text-xs text-muted-foreground">{b.action}</div>
                    </div>
                    <Badge variant="outline" className="ml-auto text-[10px] shrink-0">{b.severity}</Badge>
                  </div>
                ))}
              </div>
            )}

            {aiPlan.plan?.recommended_sequence?.length > 0 && (
              <div data-testid="ai-sequence">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">Recommended sequence</div>
                <ol className="text-sm list-decimal pl-5 space-y-0.5">
                  {aiPlan.plan.recommended_sequence.map((s, i) => <li key={i}>{s}</li>)}
                </ol>
                {aiPlan.plan.estimated_time_minutes != null && (
                  <div className="text-xs text-muted-foreground mt-2" data-testid="ai-time-estimate">
                    <Clock className="w-3 h-3 inline mr-1" />Estimated: ~{aiPlan.plan.estimated_time_minutes} minutes
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="border-t pt-4 space-y-2">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">One-click orchestrated close</div>
          <label className="flex items-center gap-2 text-sm" data-testid="ai-opt-autopost">
            <Checkbox checked={autoPost} onCheckedChange={(v) => setAutoPost(!!v)} />
            Auto-post unposted &quot;sent&quot; invoices for this period
          </label>
          <label className="flex items-center gap-2 text-sm" data-testid="ai-opt-bankmatch">
            <Checkbox checked={autoBankMatch} onCheckedChange={(v) => setAutoBankMatch(!!v)} />
            Auto-run bank smart-match
          </label>
          {autoBankMatch && (
            <div className="ml-6 max-w-md" data-testid="ai-bank-acct-wrap">
              <Label className="text-xs">Bank account</Label>
              <Select value={aiBankAcct} onValueChange={setAiBankAcct}>
                <SelectTrigger data-testid="ai-bank-acct-select"><SelectValue placeholder="Choose bank account…" /></SelectTrigger>
                <SelectContent>{accts.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="text-[11px] text-muted-foreground mt-1">
            Standard close steps (depreciation / payroll / FX reval / lock) use the same checkboxes below.
            Currently:&nbsp;
            {[opts.run_depreciation && "depreciation", opts.run_payroll && "payroll", opts.run_fx_reval && "FX reval", opts.lock_period && "lock"].filter(Boolean).join(" · ") || "none selected"}
          </div>
          <Button
            className="mt-2" onClick={onOrchestrate}
            disabled={aiRunBusy || (autoBankMatch && !aiBankAcct) || (opts.run_payroll && !opts.payroll_payment_account_id)}
            data-testid="ai-orchestrate-btn"
          >
            {aiRunBusy ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
            {aiRunBusy ? "Orchestrating…" : "▶ AI-orchestrated close"}
          </Button>
        </div>

        {aiRun && (
          <div className="border-t pt-4 space-y-3" data-testid="ai-run-result">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Post-close narrative</div>
            <div className="text-sm bg-background border rounded p-3 whitespace-pre-wrap" data-testid="ai-post-narrative">
              {aiRun.ai_narrative?.narrative}
            </div>
            {aiRun.ai_narrative?.highlights?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase text-emerald-600 tracking-wide mb-1">Highlights</div>
                <ul className="list-disc pl-5 text-sm space-y-0.5" data-testid="ai-highlights">
                  {aiRun.ai_narrative.highlights.map((h, i) => <li key={i}>{h}</li>)}
                </ul>
              </div>
            )}
            {aiRun.ai_narrative?.remaining_actions?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase text-amber-600 tracking-wide mb-1">Remaining actions</div>
                <ul className="list-disc pl-5 text-sm space-y-0.5" data-testid="ai-remaining">
                  {aiRun.ai_narrative.remaining_actions.map((h, i) => <li key={i}>{h}</li>)}
                </ul>
              </div>
            )}
            <details className="text-xs text-muted-foreground" data-testid="ai-run-details">
              <summary className="cursor-pointer">Executed steps (details)</summary>
              <pre className="mt-2 p-2 bg-muted/40 rounded overflow-x-auto text-[10px]">
                {JSON.stringify({ pre_steps: aiRun.pre_steps, close_steps: aiRun.close_result?.steps, post_readiness_score: aiRun.post_readiness_score }, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ForecastTab({ wsId }) {
  const [weeks, setWeeks] = useState(12);
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/finance/workspaces/${wsId}/reports/cash-flow-forecast?weeks=${weeks}`).then((r) => setData(r.data));
  }, [wsId, weeks]);
  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3">
        <div><Label>Horizon (weeks)</Label><Input type="number" min="1" max="52" value={weeks} onChange={(e) => setWeeks(Number(e.target.value) || 12)} data-testid="forecast-weeks-input" /></div>
      </div>
      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Kpi label="Opening cash" value={fmt(data.opening)} tone="blue" testid="forecast-opening" />
            <Kpi label="Run-rate/week (avg)" value={fmt(data.avg_weekly_net_run_rate)} tone={data.avg_weekly_net_run_rate >= 0 ? "rose" : "emerald"} testid="forecast-runrate" />
            <Kpi label={`Closing (T+${weeks}w)`} value={fmt(data.closing)} tone={data.closing >= 0 ? "emerald" : "rose"} testid="forecast-closing" />
            <Kpi label="Critical weeks" value={data.critical_weeks.length} tone={data.critical_weeks.length ? "rose" : "emerald"} testid="forecast-critical" />
          </div>
          <Card><CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs"><tr className="text-left"><th className="p-3">Week</th><th className="p-3 text-right">AR inflows</th><th className="p-3 text-right">AP outflows</th><th className="p-3 text-right">Run-rate</th><th className="p-3 text-right">Net change</th><th className="p-3 text-right">Ending</th></tr></thead>
              <tbody>{data.weeks.map((w, i) => (
                <tr key={i} className={`border-t ${w.ending_balance < 0 ? "bg-rose-50 dark:bg-rose-950/20" : ""}`} data-testid={`forecast-week-${i}`}>
                  <td className="p-3 font-mono text-xs">{w.week_start}</td>
                  <td className="p-3 text-right font-mono text-emerald-600">{w.ar_inflows ? fmt(w.ar_inflows) : "—"}</td>
                  <td className="p-3 text-right font-mono text-rose-600">{w.ap_outflows ? fmt(w.ap_outflows) : "—"}</td>
                  <td className="p-3 text-right font-mono">{fmt(w.run_rate_net)}</td>
                  <td className={`p-3 text-right font-mono ${w.net_change >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{fmt(w.net_change)}</td>
                  <td className={`p-3 text-right font-mono font-semibold ${w.ending_balance >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{fmt(w.ending_balance)}</td>
                </tr>
              ))}</tbody>
            </table>
          </CardContent></Card>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, tone, testid }) {
  const t = { emerald: "text-emerald-600 border-emerald-500/30", rose: "text-rose-600 border-rose-500/30", blue: "text-blue-600 border-blue-500/30" };
  return (
    <Card className={`border ${t[tone]}`} data-testid={testid}>
      <CardContent className="p-4"><div className="text-xs text-muted-foreground uppercase">{label}</div><div className={`text-xl font-mono font-semibold mt-1 ${t[tone]}`}>{value}</div></CardContent>
    </Card>
  );
}

function StatutoryTab({ wsId }) {
  const [fy, setFy] = useState("2025-26");
  const [quarter, setQuarter] = useState("Q1-2025-26");
  const [form3cd, setForm3cd] = useState(null);
  const [busy, setBusy] = useState(false);
  const CLAUSE_LABELS = {
    clause_16_receipts_revenue: "Clause 16 — Receipts (Revenue)",
    clause_17_expenditure_total: "Clause 17 — Total expenditure",
    clause_18_depreciation_claimed: "Clause 18 — Depreciation claimed",
    clause_18_fixed_assets_count: "Clause 18 — Active fixed assets (count)",
    clause_21_payments_disallowed: "Clause 21 — Amounts disallowed under §40",
    clause_27_tds_deducted: "Clause 27 — TDS deducted (total)",
    clause_40_net_profit: "Clause 40 — Net profit (before tax)",
  };
  const load3cd = async () => {
    setBusy(true);
    try { const { data } = await api.get(`/finance/workspaces/${wsId}/form-3cd?fiscal_year=${fy}`); setForm3cd(data); }
    catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };
  const download = async (path, filename) => {
    const url = `${api.defaults.baseURL}/finance/workspaces/${wsId}/${path}`;
    const token = localStorage.getItem("co_token");
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error("Download failed"); return; }
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = objUrl; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card><CardContent className="p-6 space-y-3">
        <h3 className="font-semibold">GSTR-9 (Annual Return)</h3>
        <p className="text-xs text-muted-foreground">Consolidated annual outward + inward supplies + tax payable.</p>
        <div className="flex items-end gap-2">
          <div><Label>Fiscal year</Label><Input value={fy} onChange={(e) => setFy(e.target.value)} data-testid="fy-input" placeholder="2025-26" /></div>
          <Button variant="outline" onClick={() => download(`gstr-9/export.csv?fiscal_year=${fy}`, `gstr-9-${fy}.csv`)} data-testid="gstr9-download-btn"><Download className="w-4 h-4 mr-2" />Download CSV</Button>
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-6 space-y-3">
        <h3 className="font-semibold">TDS Returns (26Q / 24Q)</h3>
        <p className="text-xs text-muted-foreground">26Q = Non-salary TDS · 24Q = Salary TDS. Filed quarterly.</p>
        <div className="flex items-end gap-2">
          <div><Label>Quarter</Label><Input value={quarter} onChange={(e) => setQuarter(e.target.value)} data-testid="tds-quarter-input" placeholder="Q1-2025-26" /></div>
          <Button variant="outline" onClick={() => download(`tds/26Q/export.csv?quarter=${quarter}`, `tds-26Q-${quarter}.csv`)} data-testid="tds-26q-btn"><Download className="w-4 h-4 mr-2" />26Q</Button>
          <Button variant="outline" onClick={() => download(`tds/24Q/export.csv?quarter=${quarter}`, `tds-24Q-${quarter}.csv`)} data-testid="tds-24q-btn"><Download className="w-4 h-4 mr-2" />24Q</Button>
        </div>
      </CardContent></Card>

      <Card className="lg:col-span-2"><CardContent className="p-6 space-y-3">
        <h3 className="font-semibold">Form 3CD (Tax audit clauses summary)</h3>
        <p className="text-xs text-muted-foreground">Section 44AB — key clauses computed live from the ledger.</p>
        <div className="flex items-end gap-2">
          <Button onClick={load3cd} disabled={busy} data-testid="form-3cd-run-btn">{busy ? "Loading…" : "Generate for FY " + fy}</Button>
        </div>
        {form3cd && (
          <table className="w-full text-sm mt-3" data-testid="form-3cd-table">
            <thead className="bg-muted/40"><tr className="text-left"><th className="p-2">Clause</th><th className="p-2 text-right">Value</th></tr></thead>
            <tbody>
              {Object.entries(form3cd.clauses).map(([k, v]) => (
                <tr key={k} className="border-t"><td className="p-2 text-sm">{CLAUSE_LABELS[k] || k}</td><td className="p-2 text-right font-mono">{typeof v === "number" ? (k.includes("count") ? v : fmt(v)) : String(v)}</td></tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent></Card>
    </div>
  );
}
